import { Middleware } from '@reduxjs/toolkit'
import { IApi } from '../api'
import { reloadPage } from '../helpers/reloadPage'
import { classifyCrossTabTokenChange } from '../helpers/tokenUtils'
import { sessionExpired } from './profile/actions'

/** The one `localStorage` key that carries session identity. */
const TOKEN_KEY = 'token'

/**
 * Keep a tab in step with the session the rest of the browser is in.
 *
 * The `storage` event fires in every tab of the profile *except* the one that wrote, which
 * makes it exactly the signal a tab needs and cannot otherwise get: someone signed in, signed
 * out, or rotated a token somewhere else. Without it a tab is frozen on whatever token it read
 * when it was constructed, and finds out it is stale only by making a request and being
 * refused — which is why signing out and back in on one tab made every other tab report an
 * invalid session until it was manually refreshed.
 *
 * Reacting here rather than on the 401 means the failing request never happens. It also fixes
 * the reverse case, which is the one that actually matters on a shared machine: signing out in
 * one tab used to leave every other tab sitting there rendering the account until somebody
 * clicked something. Now they all leave together.
 *
 * A middleware rather than a component effect because the teardown path has to dispatch, and
 * because it belongs to the store's lifetime rather than to any screen — the same reasoning,
 * and the same "attached once, never removed" shape, as the window listeners in
 * ./webSocketMiddleware.
 *
 * The decision itself is deliberately not here: see `classifyCrossTabTokenChange`, which is
 * pure so the rules can be tested without a DOM or a store.
 */
const crossTabSessionMiddleware =
  (api: IApi): Middleware =>
  (store) => {
    /**
     * The token we last asked to reload for, so we ask once per divergence and not once per page.
     *
     * A reload can be refused: `useBeforeUnload` calls `preventDefault` while a guarded form is
     * dirty, which RouteLeavingGuard wires up across ~15 screens, so the browser asks and the
     * user can choose to stay. `reloadPage` changes no state, so the classification stays
     * `reload` — and asking again on every later wake-up would nag with no answer that keeps
     * their work.
     *
     * Keyed on the token rather than latched outright, because "already asked" has to expire
     * when the situation does. A page-lifetime flag would also swallow the *next*, unrelated
     * divergence — decline a reload into another user's session, drift back to a newer token of
     * your own, then have that user step into a client, and the tab would go on rendering the
     * old session in silence, which is the mismatch this middleware exists to prevent.
     *
     * It also covers the ordinary case, where `reloadPage` only *initiates* unload and the page
     * keeps running for a moment: an event landing in that window cannot ask twice.
     */
    let reloadRequestedFor: string | null = null

    /**
     * Reconcile this tab against whatever the shared token is *now*.
     *
     * Storage is read back rather than taken from a `storage` event's `newValue`, because an
     * event is only a notification that something changed and never a reliable statement of
     * what it changed to. A whole-area clear names no key and carries a null value; a sign-out
     * immediately followed by a sign-in delivers a null-valued event while storage already
     * holds a good token. Reading the area is right in every one of those cases, and it is also
     * what lets the same function serve a wake-up, where there is no event at all.
     */
    const syncFromStorage = () => {
      const incoming = window.localStorage.getItem(TOKEN_KEY)
      const decision = classifyCrossTabTokenChange(incoming, api.client.token)

      switch (decision.action) {
        case 'adopt':
          // Same person, same client, newer token. Nothing on screen is wrong, so swap it in
          // and let the user carry on none the wiser.
          //
          // The open WebSocket keeps the token it connected with. The server authenticates a
          // socket at upgrade and not per message, so it stays live and correct for this user,
          // and the next reconnect picks the new token up from `api.client`. Re-authenticating
          // an open socket in place would be a bigger change than this is worth.
          api.client.setToken(decision.token)
          break

        case 'reload':
          // A different user, or the same user stepping into or out of a client. The token is
          // only half the state — the store is full of a session that no longer applies — so
          // re-derive the page rather than leave one person's screens filled with another's
          // data. This is what a user does by hand today; doing it for them is the fix.
          //
          // No loop: the fresh page constructs an ApiClient from this same token, so the first
          // sync after it lands classifies as `ignore`. And if the page never goes, see
          // `reloadRequestedFor` — the divergence persists, so the decision would too.
          if (reloadRequestedFor === incoming) break

          reloadRequestedFor = incoming
          reloadPage()
          break

        case 'signOut':
          // Signing out revokes the session for every token in it, so ours is already dead.
          // `sessionExpired` tears down locally and tells the server nothing, which is right:
          // the tab that signed out has already done the revoking.
          store.dispatch(sessionExpired())
          break

        case 'ignore':
          break
      }
    }

    const handleStorage = (event: StorageEvent) => {
      // Both filters are now about not doing needless work rather than about correctness —
      // `syncFromStorage` reads `localStorage`, so an event from `sessionStorage` (per-tab, and
      // it raises this same event) or for somebody else's key cannot move us either way. They
      // stay because classifying on every unrelated write would be pointless.
      if (event.storageArea !== window.localStorage) return

      // A null key means the whole area was cleared, which is a token change even though no key
      // is named, so it has to fall through rather than be filtered out here.
      if (event.key !== null && event.key !== TOKEN_KEY) return

      syncFromStorage()
    }

    /**
     * Catch up on a change that happened while this tab was not listening.
     *
     * `storage` is delivered only to a live page. A page in the back/forward cache is not a
     * live page and gets no replay on restore, so leaving for an external site and pressing
     * Back after someone signed in or out elsewhere reproduces the original symptom exactly —
     * the very bug this middleware exists to fix, just narrowed to a case that is easy to miss.
     * Re-reading storage on the way back in closes it.
     *
     * Same events, same "attached once, never removed" shape, and the same reasoning as
     * `handleWake` in ./webSocketMiddleware, which reconnects its socket here for the
     * neighbouring reason that a suspended tab's timers do not run.
     */
    const handleWake = () => {
      if (document.visibilityState !== 'visible') return
      syncFromStorage()
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener('pageshow', handleWake)
    document.addEventListener('visibilitychange', handleWake)

    return (next) => (action) => next(action)
  }

export default crossTabSessionMiddleware
